import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "@tanstack/react-router";
import { api, ApiRequestError } from "@/lib/api";
import { useAuth } from "@/auth/auth";
import { cs } from "@/i18n/cs";
import { Btn, Field, inputClass } from "@/design/primitives";

/**
 * Přihlášení podle artboardu p6a: logo s názvem a mono claimem nad kartou,
 * karta s akcentním okrajem a rozostřeným pozadím, primární tlačítko přes
 * celou šířku.
 */
/**
 * Kam po přihlášení. Jen relativní cesta v rámci aplikace — absolutní URL
 * a `//host` se zahazují, jinak by `?next=` byl open redirect (odkaz na login
 * chodí i z OAuth flow, takže se dá poslat komukoli).
 */
function safeNext(raw: string | null): string | null {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return null;
  return raw;
}

export function LoginPage() {
  const { session, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const next = safeNext(new URLSearchParams(window.location.search).get("next"));

  if (!loading && session) {
    // přihlášený uživatel, který sem přišel z OAuth flow, pokračuje na souhlas
    if (next) {
      window.location.replace(next);
      return null;
    }
    return <Navigate to="/" />;
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.login({ email, password });
      await refresh();
      if (next) {
        window.location.replace(next);
        return;
      }
      void navigate({ to: "/" });
    } catch (err) {
      setError(
        err instanceof ApiRequestError && err.status === 401
          ? cs.errors.unauthorized
          : err instanceof ApiRequestError && err.status === 429
            ? err.message
            : cs.common.error,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-[400px]">
        <div className="mb-6 flex items-center justify-center gap-3">
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: "var(--accent-gradient)",
              boxShadow: "var(--emphasis-accent)",
            }}
          />
          <div>
            <div className="af-display text-[22px] font-bold" style={{ color: "var(--text-primary)" }}>
              {cs.app.name}
            </div>
            <div
              className="af-mono text-[10px] tracking-[0.2em]"
              style={{ color: "var(--text-tertiary)" }}
            >
              {cs.app.claim}
            </div>
          </div>
        </div>

        <div
          className="p-[26px]"
          style={{
            borderRadius: 18,
            border: "1px solid var(--accent-border)",
            background: "var(--surface)",
            backdropFilter: "var(--surface-blur)",
          }}
        >
          <div className="af-mono mb-4 text-[10px] tracking-[0.2em]" style={{ color: "var(--accent)" }}>
            // {cs.auth.accessSubtitle}
          </div>
          <form onSubmit={submit} className="flex flex-col gap-4">
            <Field label={cs.auth.email}>
              <input
                className={inputClass}
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
              />
            </Field>
            <Field label={cs.auth.password}>
              <input
                className={inputClass}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
              />
            </Field>
            {error ? (
              <div className="text-[12px]" style={{ color: "var(--critical)" }} data-testid="login-error">
                ⚠ {error}
              </div>
            ) : null}
            <Btn type="submit" variant="primary" disabled={busy} className="mt-2 w-full py-3">
              {busy ? cs.auth.signingIn : cs.auth.signIn}
            </Btn>
          </form>
        </div>

        {import.meta.env.DEV ? (
          <p
            className="mt-4 text-center text-[11px] leading-relaxed"
            style={{ color: "var(--text-tertiary)" }}
          >
            {cs.login.hint}
          </p>
        ) : null}
      </div>
    </div>
  );
}
